import { Drawer } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { ErrorBoundary } from "@sentry/react";
import clsx from "clsx";
import { SnackbarProvider } from "notistack";
import { NuqsAdapter } from "nuqs/adapters/react-router/v6";
import { Suspense } from "react";
import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";

import { IntercomGate } from "~/IntercomGate";
import { CardDialogProvider } from "~/components/autopto/cardDialog/CardDialogProvider";
import TrialCountdownBar from "~/components/autopto/trialCountdown/TrialCountdownBar";
import ForcePasswordChangeModal from "~/components/forcePasswordChange/ForcePasswordChangeModal";
import Button from "~/components/mantine/button/Button";
import { OrderPaymentProvider } from "~/components/services/orders/OrderPaymentProvider";
import Sidebar from "~/components/sidebar/Sidebar";
import SubscriptionSync from "~/components/subscription/SubscriptionSync";
import { useAuth } from "~/contexts/AuthProvider";
import { useTeam } from "~/contexts/TeamProvider";
import { getGlobalSettings, setGlobalSettings } from "~/helpers/globalSettings";
import { isAlliAIDomain } from "~/helpers/helpers";
import { useSidebar } from "~/hooks/useSidebar.ts";
import { useSite } from "~/hooks/useSite";
import { routes } from "~/router";
import { ReditusConversionFlusher, ReditusHelmet } from "~/tracking/reditus";
import { useSegmentScript } from "~/tracking/segment";
import { FlashMessageListener } from "~/utils/flash";
import { PendingInvitationHandler } from "~/utils/invitation";

import { AppConfig } from "./AppConfig";
import { CableProvider } from "./CableContext";
import styles from "./MainView.module.scss";
import DashboardSidebar from "./components/sidebar/DashboardSidebar";
import Snackbar from "./components/ui/snackbar/Snackbar";
import { SiteContext } from "./contexts/SiteContext";
import "./helpers/load_stripe";

const ErrorFallback = ({ error }: { error: unknown }) => {
  let title = "An error occurred";
  if (error && typeof error === "object" && "status" in error) {
    switch (error.status) {
      case 404:
        title = "Page not found";
        break;
      case 403:
        title = "Access denied";
        break;
      default:
        break;
    }
  }

  return (
    <div>
      <h2>{title}</h2>
      <pre>{error instanceof Error && error.message}</pre>
    </div>
  );
};

const MainView = () => {
  const { id: siteId } = useParams();
  const location = useLocation();
  const isSitePage = location.pathname.includes("/sites/") && siteId;
  const { segmentHelmet } = useSegmentScript();
  const { user } = useAuth();
  const { team } = useTeam();
  const { pathname } = useLocation();
  const { isSidebarEmpty } = useSidebar();
  const isMobile = useMediaQuery("(max-width: 48em)");
  const isMobileOffer = isMobile && pathname === routes.autoptoOffer();
  const [isMobileNavigationOpen, setIsMobileNavigationOpen] = useState(false);

  useEffect(() => setIsMobileNavigationOpen(false), [pathname]);

  const [boundaryKey, setBoundaryKey] = useState(0);
  const [errorPath, setErrorPath] = useState<string | null>(null);

  const {
    data: siteData,
    isLoading: isLoadingSite,
    error: siteError,
    refetch: refetchSite,
  } = useSite(siteId);

  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    if (typeof window === "undefined") return true;

    const gs = getGlobalSettings();

    return gs.isSidebarOpen ?? true;
  });

  const [isDashboardSidebarOpen, setIsDashboardSidebarOpen] = useState(() => {
    if (typeof window === "undefined") return Boolean(isSitePage);

    const gs = getGlobalSettings();

    return gs.isDashboardSidebarOpen ?? Boolean(isSitePage);
  });

  useEffect(() => {
    setGlobalSettings({ isSidebarOpen });
  }, [isSidebarOpen]);

  useEffect(() => {
    setGlobalSettings({ isDashboardSidebarOpen });
  }, [isDashboardSidebarOpen]);

  /**
   * ErrorBoundary reset-on-navigation (Sentry workaround).
   *
   * Problem:
   * - @sentry/react ErrorBoundary "sticks" once it catches an error: it keeps rendering the fallback
   *   and does NOT automatically recover when the user navigates to another route.
   * - Previously we used `key={location.pathname}` on the boundary to force a reset on every navigation,
   *   but that causes full unmount/remount of the entire outlet subtree on every route change.
   *   This was breaking long-lived UI state (e.g. ActionCable subscriptions, react-query observers, etc.).
   *
   * Desired behavior:
   * - Keep the boundary persistent during normal navigation (no forced remounts).
   * - BUT if a route throws and we are currently showing the fallback, automatically "unstick" the boundary
   *   as soon as the user navigates away to a different pathname.
   *
   * Approach:
   * - Store the pathname where the error happened (errorPath).
   * - Only when we are in fallback (errorPath != null) AND pathname changes, bump boundaryKey.
   * - boundaryKey is used as React key for the inner boundary around <Outlet/>, forcing a reset only in the
   *   error → navigation-away scenario.
   *
   * Why not use react-error-boundary/resetKeys?
   * - react-error-boundary supports resetKeys out of the box, but switching global error boundary library
   *   in an existing app is risky: it may change reporting behavior, integrations, and how errors are captured
   *   across the whole project.
   * - This local workaround keeps Sentry integration intact and achieves the same UX outcome as resetKeys,
   *   without the "always remount on navigation" side effects.
   */
  useEffect(() => {
    if (!errorPath) return;
    if (pathname === errorPath) return;

    setBoundaryKey((k) => k + 1);
    setErrorPath(null);
  }, [pathname, errorPath]);

  const siteContextValue = useMemo(
    () => ({
      site: siteData || null,
      isLoadingSite,
      siteError,
      refetchSite,
    }),
    [siteData, isLoadingSite, siteError, refetchSite],
  );

  if (!user) return null;

  const shouldShowIntercom =
    team && Boolean(AppConfig.INTERCOM_APP_ID) && isAlliAIDomain();

  const content = (
    <CableProvider>
      {segmentHelmet}
      <PendingInvitationHandler />
      <ForcePasswordChangeModal />
      <ReditusHelmet />
      <ReditusConversionFlusher />
      {shouldShowIntercom && <IntercomGate user={user} team={team} />}
      <ErrorBoundary fallback={({ error }) => <ErrorFallback error={error} />}>
        <CardDialogProvider>
          <OrderPaymentProvider>
            <div
              className={
                isSidebarEmpty || !isSitePage
                  ? clsx(styles.mainWraper, styles.singleSidebar, {
                      [styles.mobileOffer]: isMobileOffer,
                    })
                  : clsx(styles.mainWraper, styles.doubleSidebar)
              }
            >
              {!isSidebarEmpty && !isMobileOffer && (
                <div
                  className={clsx(styles.sidebarContainer, {
                    [styles.sidebarOpen]: isSidebarOpen,
                    [styles.sidebarClosed]: !isSidebarOpen,
                  })}
                >
                  <Sidebar
                    isSidebarOpen={isSidebarOpen}
                    setIsSidebarOpen={setIsSidebarOpen}
                  />
                </div>
              )}
              {isSitePage && (
                <div
                  className={clsx(styles.dashboardSidebarContainer, {
                    [styles.dashboardSidebarOpen]: isDashboardSidebarOpen,
                    [styles.dashboardSidebarClosed]: !isDashboardSidebarOpen,
                  })}
                >
                  <DashboardSidebar
                    isSidebarOpen={isDashboardSidebarOpen}
                    setIsSidebarOpen={setIsDashboardSidebarOpen}
                  />
                </div>
              )}
              {isMobileOffer && !isSidebarEmpty && (
                <Drawer
                  opened={isMobileNavigationOpen}
                  onClose={() => setIsMobileNavigationOpen(false)}
                  title="Navigation"
                  classNames={{
                    inner: styles.mobileDrawerInner,
                    overlay: styles.mobileDrawerOverlay,
                    content: styles.mobileDrawerContent,
                    header: styles.mobileDrawerHeader,
                    body: styles.mobileDrawerBody,
                    close: styles.mobileDrawerClose,
                  }}
                  position="left"
                  size="17rem"
                >
                  <Sidebar
                    isSidebarOpen
                    setIsSidebarOpen={() => setIsMobileNavigationOpen(false)}
                  />
                </Drawer>
              )}
              <div className={styles.contentColumn}>
                {isMobileOffer && !isSidebarEmpty && (
                  <div className={styles.mobileNavigation}>
                    <Button
                      variant="default"
                      aria-label="Open navigation"
                      onClick={() => setIsMobileNavigationOpen(true)}
                    >
                      Menu
                    </Button>
                  </div>
                )}
                {/* Above the scrolling container: stays visible on every page. */}
                <TrialCountdownBar />
                <SubscriptionSync />
                <div className={styles.mainContent} id="scrolling-container">
                  <ErrorBoundary
                    key={boundaryKey}
                    onError={() => setErrorPath(pathname)}
                    fallback={({ error }) => <ErrorFallback error={error} />}
                  >
                    <NuqsAdapter>
                      <Suspense>
                        <Outlet />
                      </Suspense>
                    </NuqsAdapter>
                  </ErrorBoundary>
                </div>
              </div>
            </div>
          </OrderPaymentProvider>
        </CardDialogProvider>
        <SnackbarProvider
          maxSnack={9}
          Components={{
            error: Snackbar,
            success: Snackbar,
            warning: Snackbar,
            info: Snackbar,
            default: Snackbar,
          }}
          TransitionProps={{ direction: "left" }}
          anchorOrigin={{ horizontal: "right", vertical: "top" }}
          autoHideDuration={5000}
        >
          <FlashMessageListener />
        </SnackbarProvider>
      </ErrorBoundary>
    </CableProvider>
  );

  // Always wrap in SiteContext.Provider to keep the component tree stable across navigation.
  // If the wrapper were conditional (present on site pages, absent elsewhere), React would
  // remount the entire subtree — including Sidebar and CableProvider — whenever the user
  // navigates between site and non-site routes.
  return (
    <SiteContext.Provider
      value={isSitePage && siteId ? siteContextValue : null}
    >
      {content}
    </SiteContext.Provider>
  );
};

export default MainView;
